// pages/ShopsPage.tsx
import { useState, useMemo } from "react";
import { Plus, Search, MoreVertical, Store, Filter, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import PageLayout from "../../../layouts/PageListLayout";
import FlexContainer from "../../../components/ui/FlexContainer";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import { useAuth, useShops } from "@cabukaka/hooks";
import ShopsList from "../../../components/features/shops/ShopsList";
import { Shop } from "@cabukaka/types";

export default function ShopsPage() {
    const navigate = useNavigate();
    const {user} = useAuth()
    const role = user?.role;
    const { shops, deleteShop, deleteManyShops } = useShops();
    const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCity, setSelectedCity] = useState<string>("");
    const [selectedStatus, setSelectedStatus] = useState<string>("");
    const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
    const [shopToDelete, setShopToDelete] = useState<Shop | null>(null);
    const [itemsToDelete, setItemsToDelete] = useState<string[] | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    // Récupérer les villes uniques
    const citiesOptions = useMemo(() => {
        const cities = new Set(shops.map(s => s.location?.city).filter(Boolean));
        return Array.from(cities);
    }, [shops]);

    // Filtrage des boutiques
    const filteredShops = useMemo(() => {
        let result = shops;

        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase().trim();
            result = result.filter(s =>
                s.name?.toLowerCase().includes(searchLower) ||
                s.description?.toLowerCase().includes(searchLower) ||
                s.location?.city?.toLowerCase().includes(searchLower)
            );
        }

        if (selectedCity) {
            result = result.filter(s => s.location?.city === selectedCity);
        }

        if (selectedStatus) {
            result = result.filter(s => selectedStatus === "open" ? s.open : !s.open);
        }

        return result;
    }, [shops, searchTerm, selectedCity, selectedStatus]);

    const stats = useMemo(() => ({
        total: shops.length,
        open: shops.filter(s => s.open).length,
        closed: shops.filter(s => !s.open).length
    }), [shops]);

    const handleDelete = async () => {
        if (!shopToDelete?.id) return;
        setIsDeleting(true);
        try {
            await deleteShop(shopToDelete.id);
            setShopToDelete(null);
            alertSuccess("Boutique supprimée avec succès");
        } catch (err) {
            alertError("Erreur lors de la suppression");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteMany = async () => {
        if (!itemsToDelete || itemsToDelete.length === 0) return;
        try {
            await deleteManyShops(itemsToDelete);
            setItemsToDelete(null);
            alertSuccess(`${itemsToDelete.length} boutique(s) supprimée(s)`);
        } catch (err) {
            alertError("Erreur lors de la suppression multiple");
        }
    };

    const clearFilters = () => {
        setSelectedCity("");
        setSelectedStatus("");
        setSearchTerm("");
    };

    const hasActiveFilters = selectedCity || selectedStatus || searchTerm;

    const menuItems: Menu[] = [
        {
            label: "Ajouter une boutique",
            icon: Store,
            onClick: () => { navigate(`/${role}/shops/new`); setIsAddMenuOpen(false); }
        }
    ];

    const statusOptions = [
        { value: "open", label: "Ouvert" },
        { value: "closed", label: "Fermé" }
    ];

    return (
        <PageLayout
            title="Gestion des boutiques"
            description={`${filteredShops.length} boutique${filteredShops.length > 1 ? 's' : ''} affiché${filteredShops.length > 1 ? 's' : ''}`}
            actions={
                <FlexContainer className="animate-fade-in-up w-full">
                    <button
                        onClick={() => setIsAddMenuOpen(true)}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-primary to-primary/80 text-white rounded-xl"
                    >
                        <Plus size={18} /> Ajouter
                    </button>

                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all ${showFilters ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    >
                        <Filter size={18} /> Filtrer
                        {hasActiveFilters && <span className="ml-1 w-2 h-2 bg-red-500 rounded-full" />}
                    </button>
                </FlexContainer>
            }
            menu={{
                isOpen: isAddMenuOpen,
                onClose: () => setIsAddMenuOpen(false),
                title: "Gestion des boutiques",
                icon: <Store className="text-primary" size={20} />,
                items: menuItems
            }}
        >
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5">
                    <p className="text-xs font-semibold text-blue-700/70">Total boutiques</p>
                    <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-5">
                    <p className="text-xs font-semibold text-emerald-700/70">Ouvertes</p>
                    <p className="text-2xl font-bold text-emerald-700">{stats.open}</p>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-5">
                    <p className="text-xs font-semibold text-red-700/70">Fermées</p>
                    <p className="text-2xl font-bold text-red-700">{stats.closed}</p>
                </div>
            </div>

            {/* Recherche */}
            <div className="relative mb-4">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    placeholder="Rechercher une boutique..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                />
            </div>

            {/* Filtres */}
            {showFilters && (
                <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="font-medium text-gray-700">Filtres</h3>
                        {hasActiveFilters && (
                            <button onClick={clearFilters} className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1">
                                <X size={14} /> Effacer tout
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                            <select
                                value={selectedCity}
                                onChange={(e) => setSelectedCity(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            >
                                <option value="">Toutes les villes</option>
                                {citiesOptions.map(city => (
                                    <option key={city} value={city}>{city}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                            <select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            >
                                <option value="">Tous les statuts</option>
                                {statusOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            )}

            {/* Liste */}
            <div>
                <ShopsList
                    shops={filteredShops}
                    selectable={true}
                    onAction={(s) => setSelectedShop(s)}
                    selectActions={[{
                        label: "Supprimer",
                        onClick: (selected) => setItemsToDelete(selected.map(i => i.id)),
                        className: "bg-red-600 text-white"
                    }]}
                />
            </div>

            {/* Menu modal */}
            {selectedShop && (
                <MenuModal
                    title={selectedShop.name}
                    isOpen={!!selectedShop}
                    onClose={() => setSelectedShop(null)}
                    icon={<Store className="text-primary" size={20} />}
                    menu={[
                        { label: "Voir détails", icon: Search, onClick: () => navigate(`/${role}/shops/details?id=${selectedShop.id}`) },
                        { label: "Modifier", icon: Store, onClick: () => navigate(`/${role}/shops/update?id=${selectedShop.id}`) },
                        { label: "Supprimer", icon: MoreVertical, onClick: () => { setShopToDelete(selectedShop); setSelectedShop(null); } }
                    ]}
                />
            )}

            {/* Modals suppression */}
            <DeleteConfirmationModal 
                isOpen={!!shopToDelete} 
                onClose={() => setShopToDelete(null)} 
                onConfirm={handleDelete} 
                title="Supprimer la boutique" 
                message={`Supprimer "${shopToDelete?.name}" ?`} 
                confirmText={isDeleting ? "Suppression..." : "Supprimer"} 
            />
            <DeleteConfirmationModal 
                isOpen={!!itemsToDelete && itemsToDelete.length > 0} 
                onClose={() => setItemsToDelete(null)} 
                onConfirm={handleDeleteMany} 
                title="Suppression multiple" 
                message={`Supprimer ${itemsToDelete?.length} boutique(s) ?`} 
            />
        </PageLayout>
    );
}